import React, { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { useDebouncedEffect } from "hooks/useDebouncedEffect"

import { REQ } from "constants/api/requests"

import useApi from "util/api"

interface Game {
  value: string
  name: string
  producer?: string
  provider?: string
}

interface Provider {
  value: string
  name: string
  producer?: string
  provider?: string
}

interface FreeSpinGamesPickerProps {
  selectedGameIds: string[]
  onGameIdsChange: (gameIds: string[]) => void
  tenant: string
  title?: string
  /**
   * SOFT-736 — restrict the whole picker to producers whose games carry bet ladders (NUX/DARK):
   * the only ones a free-spins stake can resolve on, so anything else here ends in a refused
   * save. Set by the FREE-SPINS games selection; the eligibility pickers (allowed/restricted
   * games, game groups) leave it off and see the full catalog.
   */
  ladderProducersOnly?: boolean
  /**
   * One game at most. The provider is handed the round count per LAUNCH, keyed by a bonus name
   * that carries the game id, so the same grant announced on a second game is a second bonus at
   * the provider — the pool is spent once here and twice there.
   */
  singleSelect?: boolean
}

/** Rows fetched per request. The server clamps anything above its own max. */
const PAGE_SIZE = 100

/** Debounce on the search box — each keystroke was previously a full catalog scan. */
const SEARCH_DEBOUNCE_MS = 400

/**
 * Game picker for bonus eligibility (free-spin games, allowed games, restricted games) and for
 * game groups.
 *
 * The catalog runs to tens of thousands of rows on NUX, so the list is PAGED: one page is
 * fetched at a time and appended via "Load more", and the header reports the server's `total`
 * for the current filter rather than however many rows happen to be loaded. "Select all"
 * resolves the full id list for the filter server-side — it must never mean "the rows currently
 * on screen", because a partial `allowed_game_ids` silently makes every unlisted game
 * ineligible for the bonus.
 */
export const FreeSpinGamesPicker: React.FC<FreeSpinGamesPickerProps> = ({
  selectedGameIds,
  onGameIdsChange,
  tenant,
  title,
  ladderProducersOnly = false,
  singleSelect = false
}) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [providers, setProviders] = useState<Provider[]>([])
  const [games, setGames] = useState<Game[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [selectedProvider, setSelectedProvider] = useState<string>("")
  const [searchInput, setSearchInput] = useState<string>("")
  const [searchQuery, setSearchQuery] = useState<string>("")
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [selectingAll, setSelectingAll] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Bumped on every request so a slow in-flight response cannot overwrite the list belonging to
  // a newer filter (the fetches were previously unguarded and raced on fast filter changes).
  const requestGenRef = useRef(0)

  // Debounce the search box into the value the fetch effect actually depends on.
  useDebouncedEffect(() => setSearchQuery(searchInput.trim()), [searchInput], SEARCH_DEBOUNCE_MS)

  /**
   * The filter, spelled once: the list, the ids ("select all") and the provider dropdown must
   * all describe the same catalog, or the dropdown offers studios whose game list then comes
   * back empty — and select-all could return ids the list is not allowed to show.
   *
   * `hasFreespins` is pinned OFF and must be sent explicitly: the endpoint treats an absent
   * param as `true`. `Game.has_freespins` says the PROVIDER supports free-spin bonuses on that
   * game — nothing about whether a bonus may count it — and it is `false` for the whole NUX
   * catalog (~16k games), so filtering on it left the eligibility lists all but empty on a
   * NUX-served tenant. The free-spins selection narrows with `ladderOnly` instead, which is the
   * question that actually applies there: can a stake resolve on this game.
   */
  const filterParams = useCallback(() => {
    const params: Record<string, string | boolean | number> = { tenant, hasFreespins: false }
    if (ladderProducersOnly) params.ladderOnly = true
    if (selectedProvider) params.provider = selectedProvider
    if (searchQuery) params.query = searchQuery
    return params
  }, [tenant, ladderProducersOnly, selectedProvider, searchQuery])

  const fetchGames = useCallback(
    async (targetPage: number) => {
      const gen = ++requestGenRef.current
      if (targetPage === 1) {
        setLoading(true)
      } else {
        setLoadingMore(true)
      }

      try {
        const { data } = await useApi.get(REQ.ADMIN_DASHBOARD.GAMES.FREE_SPIN_GAMES, {
          params: { ...filterParams(), page: targetPage, pageSize: PAGE_SIZE }
        })

        // A stale response from a superseded filter must not touch the list.
        if (gen !== requestGenRef.current) return

        if (data?.success && data?.response) {
          const pageGames: Game[] = data.response.games ?? []
          setGames((prev) => (targetPage === 1 ? pageGames : [...prev, ...pageGames]))
          setTotal(data.response.total ?? 0)
          setPage(targetPage)
          setError(null)
        }
      } catch (err: any) {
        if (gen !== requestGenRef.current) return
        setError(t("bonus2.config.loadGamesFailed", { reason: err.message || (t("bonus2.config.unknownError") as string) }) as string)
      } finally {
        if (gen === requestGenRef.current) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    [filterParams]
  )

  // The studio list must be drawn from the same catalog as the games, so it carries the same
  // producer bounds and the same relaxed has_freespins — otherwise it offers studios whose
  // game list comes back empty.
  const fetchProviders = useCallback(async () => {
    try {
      const params: Record<string, string | boolean> = { tenant, hasFreespins: false }
      if (ladderProducersOnly) params.ladderOnly = true

      const { data } = await useApi.get(REQ.ADMIN_DASHBOARD.GAMES.FREE_SPIN_GAME_PROVIDERS, { params })
      if (data?.success && data?.response) {
        setProviders(data.response)
        // Drop the selected provider when it is not in the newly filtered list.
        setSelectedProvider((current) => (current && !data.response.some((p: Provider) => p.value === current) ? "" : current))
      }
    } catch (err: any) {
      setError(t("bonus2.config.loadProvidersFailed", { reason: err.message || (t("bonus2.config.unknownError") as string) }) as string)
    }
  }, [tenant, ladderProducersOnly])

  useEffect(() => {
    fetchProviders()
  }, [fetchProviders])

  // Any filter change restarts the list at page 1.
  useEffect(() => {
    fetchGames(1)
  }, [fetchGames])

  const toggleGame = (gameId: string) => {
    if (selectedGameIds.includes(gameId)) {
      onGameIdsChange(singleSelect ? [] : selectedGameIds.filter((id) => id !== gameId))
    } else {
      onGameIdsChange(singleSelect ? [gameId] : [...selectedGameIds, gameId])
    }
  }

  const selectedSet = new Set(selectedGameIds)
  const loadedIds = games.map((g) => g.value)
  const loadedAllSelected = games.length > 0 && loadedIds.every((id) => selectedSet.has(id))
  // "Everything matching is selected" can only be claimed when the whole filter is loaded and
  // selected — comparing counts against the loaded page is what made the old checkbox lie.
  const allMatchingSelected = total > 0 && games.length === total && loadedAllSelected

  /**
   * Select every game matching the CURRENT filter, not just the loaded pages — the ids come
   * from the server. Merged into (not replacing) the existing selection so a list built up
   * across several filters is preserved.
   */
  const selectAllMatching = async () => {
    setSelectingAll(true)
    try {
      const { data } = await useApi.get(REQ.ADMIN_DASHBOARD.GAMES.FREE_SPIN_GAME_IDS, { params: filterParams() })
      if (!data?.success || !data?.response) {
        toast.error(t("bonus2.config.selectAllFailed") as string)
        return
      }
      if (data.response.truncated) {
        toast.error(t("bonus2.config.selectAllTooMany", { total: data.response.total }) as string)
        return
      }
      const merged = new Set(selectedGameIds)
      for (const id of data.response.ids as string[]) {
        merged.add(id)
      }
      onGameIdsChange(Array.from(merged))
    } catch (err: any) {
      toast.error(t("bonus2.config.selectAllFailedReason", { reason: err.message || (t("bonus2.config.unknownError") as string) }) as string)
    } finally {
      setSelectingAll(false)
    }
  }

  /** Deselect only what is currently shown, leaving selections made under other filters. */
  const deselectShown = () => {
    const loaded = new Set(loadedIds)
    onGameIdsChange(selectedGameIds.filter((id) => !loaded.has(id)))
  }

  const clearAll = () => {
    onGameIdsChange([])
  }

  const resetFilters = () => {
    setSelectedProvider("")
    setSearchInput("")
    setSearchQuery("")
  }

  const hasActiveFilter = Boolean(selectedProvider || searchQuery)

  return (
    <div className="space-y-4">
      {/* Header — only for callers that do not already name the selection themselves. */}
      {title && (
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">
            {title} ({selectedGameIds.length} selected)
          </h3>
          {selectedGameIds.length > 0 && (
            <button type="button" onClick={clearAll} className="text-sm text-rose-400 hover:text-rose-300">
              {singleSelect ? t("bonus2.config.clear") : t("bonus2.config.clearAll")}
            </button>
          )}
        </div>
      )}

      {/* A template saved before the one-game rule. Picking any game replaces the whole set, so
          the operator is told rather than left to wonder why the list disagrees with the form. */}
      {singleSelect && selectedGameIds.length > 1 && (
        <div className="rounded border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs text-amber-200">
          This bonus names {selectedGameIds.length} games. Free spins run on one game — pick it below to replace them.
        </div>
      )}

      {/* Filters */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Provider Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t("bonus2.config.filterByProvider")}</label>
            <select
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
              className="w-full bg-[#0C0C1A] text-white border border-gray-600 rounded px-3 py-2 focus:outline-none focus:border-purple-500"
            >
              <option value="">{t("bonus2.config.allProviders")}</option>
              {providers.map((provider) => (
                <option key={provider.value} value={provider.value}>
                  {provider.name}
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t("bonus2.config.searchGame")}</label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("bonus2.config.searchByGameName")}
              className="w-full bg-[#0C0C1A] text-white border border-gray-600 rounded px-3 py-2 focus:outline-none focus:border-purple-500"
            />
          </div>
        </div>
      </div>

      {/* Error */}
      {error && <div className="bg-rose-500/10 border border-rose-500 text-rose-400 px-4 py-2 rounded">{error}</div>}

      {/* Loading — first page only; "Load more" keeps the current list on screen. */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          <p className="text-gray-400 mt-2">{t("bonus2.config.loadingGames")}</p>
        </div>
      )}

      {/* Games List */}
      {!loading && games.length > 0 && (
        <div className="space-y-3">
          {/* Filter total + select-all. The count is the SERVER's total for the filter, not the
              number of loaded rows — the old header reported the loaded rows and so read
              "Select All (500 games)" no matter how large the catalog was. */}
          <div className="flex items-center justify-between bg-[#0C0C1A] border border-gray-700 rounded px-4 py-2">
            <span className="text-sm font-medium text-white">
              {t("bonus2.config.gamesMatchFilter", { count: total })}
              <span className="ml-2 text-xs text-gray-400">
                ({t("bonus2.config.showingCount", { count: games.length })}
                {!singleSelect && allMatchingSelected ? t("bonus2.config.allSelectedSuffix") : ""})
              </span>
            </span>
            {/* Never offered when only one game may be chosen. */}
            {singleSelect ? (
              <span className="text-xs text-gray-400">{t("bonus2.config.pickOne")}</span>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={selectAllMatching}
                  disabled={selectingAll || allMatchingSelected}
                  className="text-sm text-purple-400 hover:text-purple-300 disabled:text-gray-500"
                >
                  {selectingAll ? (t("bonus2.config.selecting") as string) : (t("bonus2.config.selectAllN", { total }) as string)}
                </button>
                {loadedAllSelected && (
                  <button type="button" onClick={deselectShown} className="text-sm text-rose-400 hover:text-rose-300">
                    {t("bonus2.config.deselectShown")}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Game Items */}
          <div className="max-h-96 overflow-y-auto space-y-2 border border-gray-700 rounded p-2 bg-[#0A0A14]">
            {games.map((game) => (
              <div
                key={game.value}
                className="flex items-center justify-between bg-[#0C0C1A] border border-gray-700 rounded px-4 py-3 hover:border-purple-500 transition-colors"
              >
                <label className="flex items-center cursor-pointer flex-1">
                  <input
                    type={singleSelect ? "radio" : "checkbox"}
                    name={singleSelect ? "free-spins-game" : undefined}
                    checked={selectedSet.has(game.value)}
                    onChange={() => toggleGame(game.value)}
                    className={`w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 focus:ring-purple-500 ${singleSelect ? "" : "rounded"}`}
                  />
                  <div className="ml-3 flex-1">
                    <div className="text-white font-medium">{game.name}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-400 font-mono">ID: {game.value}</span>
                      {game.producer && <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded">{game.producer}</span>}
                      {game.provider && (
                        <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">{game.provider}</span>
                      )}
                    </div>
                  </div>
                </label>
              </div>
            ))}

            {games.length < total && (
              <button
                type="button"
                onClick={() => fetchGames(page + 1)}
                disabled={loadingMore}
                className="w-full py-2 text-sm text-purple-400 hover:text-purple-300 disabled:text-gray-500"
              >
                {loadingMore ? (t("bonus2.config.loadingMore") as string) : (t("bonus2.config.loadMoreN", { count: total - games.length }) as string)}
              </button>
            )}
          </div>
        </div>
      )}

      {/* No Results */}
      {!loading && games.length === 0 && hasActiveFilter && (
        <div className="text-center py-8 text-gray-400">
          <p>{t("bonus2.config.noGamesFoundMatching")}</p>
          <button type="button" onClick={resetFilters} className="mt-2 text-purple-400 hover:text-purple-300 text-sm">
            {t("bonus2.config.clearFilters")}
          </button>
        </div>
      )}

      {/* Nothing to offer at all — an unfiltered list is the whole catalog, so this is a real
          empty catalog (no producer served, or games not synced yet), not a prompt to filter. */}
      {!loading && games.length === 0 && !hasActiveFilter && (
        <div className="text-center py-8 text-gray-400">
          <p>{t("bonus2.config.noGamesAvailableFor")}</p>
        </div>
      )}

      {/* Selected Games Summary */}
      {selectedGameIds.length > 0 && (
        <div className="bg-purple-500/10 border border-purple-500 rounded p-4">
          <h4 className="text-sm font-medium text-purple-300 mb-2">Selected Game IDs ({selectedGameIds.length}):</h4>
          <div className="text-xs text-gray-300 font-mono max-h-32 overflow-y-auto">{selectedGameIds.join(", ")}</div>
        </div>
      )}
    </div>
  )
}
